import { ENDPOINTS } from "@constants";
import AbstractService from "./AbstractService";
import FeatureFlagService from "./FeatureFlagService";
import NexusService from "./NexusService";

export default class ContactService extends AbstractService {
	static endpoint = ENDPOINTS.contact;

	static contactsApi = NexusService.contactsApi;

	static apiCoreEndpointsFeature = "use_api_core_endpoints_contacts";

	static getContact(id) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			return this.contactsApi.getContact({ id }).then(response => ({ data: response }));
		}

		return this.get({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static getContactByEmail(email) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			return this.contactsApi.lookupContact({ email }).then(response => ({ data: response }));
		}

		return this.get({
			endpoint: `${this.endpoint}?email=${encodeURIComponent(email)}`,
		});
	}

	static getAll(
		{
			clientId,
			isOrganization,
			includeCoordinators,
			canSupervise,
			internalCoordClientId, // only used for internal coordinator purposes
			...rest
		},
		getCancelToken,
	) {
		const endpoint = `${isOrganization ? ENDPOINTS.organizations : ENDPOINTS.clients}/${clientId}/${this.endpoint}`;
		return this.get({
			endpoint,
			getCancelToken,
			queryParams: {
				minimalData: true,
				clientId: canSupervise ? internalCoordClientId : undefined,
				includeCoordinators,
				canSupervise,
				...rest,
			},
		});
	}

	static editContact({
		id,
		firstName,
		lastName,
		email,
		phone,
		streetAddress,
		city,
		state,
		zip,
		isAdmin,
		isCoordinator,
		organizationId,
		IsViewOnly,
		clientId,
	}) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			const updateReq = {
				id,
				updateContactPayload: {
					Id: id,
					FirstName: firstName,
					LastName: lastName,
					EmailAddress: email,
					MobilePhone: phone,
					StreetAddress1: streetAddress,
					City: city,
					ZipCode: zip,
					IsAdmin: isAdmin,
				}
			}

			return this.contactsApi.updateContact(updateReq).then(response => ({ data: response }));
		}

		const endpoint = `${this.endpoint}/${id}`;

		return this.put({
			endpoint,
			data: {
				firstName,
				lastName,
				city,
				isAdmin,
				isCoordinator,
				IsViewOnly,
				organizationId,
				clientId,
				ContactName: `${lastName}, ${firstName}`,
				EmailAddress: email,
				MobilePhone: phone,
				StreetAddress1: streetAddress,
				StateId: state,
				ZipCode: zip,
			},
		});
	}

	static createContact(data) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			if (data.organizationId !== null) {
				const request = {
					organizationId: data.organizationId,
					createOrganizationContactPayload: {},
				}
				if (Object.hasOwn(data, "organizationId")) {
					request.createOrganizationContactPayload.OrganizationId = data.organizationId;
				}
				if (Object.hasOwn(data, "EmailAddress")) {
					request.createOrganizationContactPayload.EmailAddress = data.EmailAddress;
				}
				if (Object.hasOwn(data, "StreetAddress1")) {
					request.createOrganizationContactPayload.StreetAddress1 = data.StreetAddress1;
				}
				if (Object.hasOwn(data, "City")) {
					request.createOrganizationContactPayload.City = data.City;
				}
				if (Object.hasOwn(data, "Zipcode")) {
					request.createOrganizationContactPayload.Zipcode = data.Zipcode;
				}
				if (Object.hasOwn(data, "StateId")) {
					request.createOrganizationContactPayload.StateId = data.StateId;
				}
				if (Object.hasOwn(data, "MobilePhone")) {
					request.createOrganizationContactPayload.MobilePhone = data.MobilePhone;
				}
				if (Object.hasOwn(data, "AllowTextAlert")) {
					request.createOrganizationContactPayload.AllowTextAlert = data.AllowTextAlert;
				}
				if (Object.hasOwn(data, "IsEmergenyContact")) {
					request.createOrganizationContactPayload.IsEmergenyContact = data.IsEmergenyContact;
				}
				if (Object.hasOwn(data, "CloudFilePictureId")) {
					request.createOrganizationContactPayload.CloudFilePictureId = data.CloudFilePictureId;
				}
				if (Object.hasOwn(data, "IsAdmin")) {
					request.createOrganizationContactPayload.IsAdmin = data.IsAdmin;
				}
				if (Object.hasOwn(data, "IsCoordinator")) {
					request.createOrganizationContactPayload.IsCoordinator = data.IsCoordinator;
				}
				if (Object.hasOwn(data, "LastName")) {
					request.createOrganizationContactPayload.LastName = data.LastName;
				}
				if (Object.hasOwn(data, "LocationId")) {
					request.createOrganizationContactPayload.LocationId = data.LocationId;
				}
				if (Object.hasOwn(data, "FullName")) {
					request.createOrganizationContactPayload.FullName = data.FullName;
				}
				return this.contactsApi.createOrganizationContact(request).then((response) => ({data: response}));
			}
			const request = {
				client: data.clientId,
				createClientContactPayload: {},
			}
			if (Object.hasOwn(data, "organizationId")) {
				request.createClientContactPayload.OrganizationId = data.organizationId;
			}
			if (Object.hasOwn(data, "EmailAddress")) {
				request.createClientContactPayload.EmailAddress = data.EmailAddress;
			}
			if (Object.hasOwn(data, "StreetAddress1")) {
				request.createClientContactPayload.StreetAddress1 = data.StreetAddress1;
			}
			if (Object.hasOwn(data, "City")) {
				request.createClientContactPayload.City = data.City;
			}
			if (Object.hasOwn(data, "Zipcode")) {
				request.createClientContactPayload.Zipcode = data.Zipcode;
			}
			if (Object.hasOwn(data, "StateId")) {
				request.createClientContactPayload.StateId = data.StateId;
			}
			if (Object.hasOwn(data, "MobilePhone")) {
				request.createClientContactPayload.MobilePhone = data.MobilePhone;
			}
			if (Object.hasOwn(data, "AllowTextAlert")) {
				request.createClientContactPayload.AllowTextAlert = data.AllowTextAlert;
			}
			if (Object.hasOwn(data, "IsEmergenyContact")) {
				request.createClientContactPayload.IsEmergenyContact = data.IsEmergenyContact;
			}
			if (Object.hasOwn(data, "CloudFilePictureId")) {
				request.createClientContactPayload.CloudFilePictureId = data.CloudFilePictureId;
			}
			if (Object.hasOwn(data, "IsAdmin")) {
				request.createClientContactPayload.IsAdmin = data.IsAdmin;
			}
			if (Object.hasOwn(data, "IsCoordinator")) {
				request.createClientContactPayload.IsCoordinator = data.IsCoordinator;
			}
			if (Object.hasOwn(data, "LastName")) {
				request.createClientContactPayload.LastName = data.LastName;
			}
			if (Object.hasOwn(data, "LocationId")) {
				request.createClientContactPayload.LocationId = data.LocationId;
			}
			if (Object.hasOwn(data, "FullName")) {
				request.createClientContactPayload.FullName = data.FullName;
			}
			return this.contactsApi.createClientContact(request).then((response) => ({data: response}));

		}
		const endpoint = `${data.organizationId ? ENDPOINTS.organizations : ENDPOINTS.clients}/${data.organizationId || data.clientId}/${this.endpoint}`;
		return this.post({
			endpoint,
			data,
		});
	}

	static mergeContact({ Id, clientId, organizationId }) {
		return this.put({
			endpoint: `${organizationId ? ENDPOINTS.organizations : ENDPOINTS.clients}/${organizationId || clientId}/${this.endpoint}`,
			data: { Id },
		});
	}

	static deleteClientContact(clientId, contactId) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			return this.contactsApi.deleteClientContact({ clientId, contactId }).then(response => ({ data: response }));
		}
		return this.del({
			endpoint: `${ENDPOINTS.clients}/${clientId}/${this.endpoint}/${contactId}`,
		});
	}

	static getContactsByClientId(clientId, queryParams) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			const getReq = {
				clientId,
				contactPaginatedListQueryPayload: {},
			}
			if (Object.hasOwn(queryParams, "pageNumber")) {
				getReq.contactPaginatedListQueryPayload.PageNumber = queryParams.pageNumber;
			}
			if (Object.hasOwn(queryParams, "displayCount")) {
				getReq.contactPaginatedListQueryPayload.DisplayCount = queryParams.displayCount;
			}
			if (Object.hasOwn(queryParams, "status")) {
				getReq.contactPaginatedListQueryPayload.Status = queryParams.status;
			}
			if (Object.hasOwn(queryParams, "search")) {
				getReq.contactPaginatedListQueryPayload.Search = queryParams.search;
			}

			return this.contactsApi.getClientContacts(getReq).then(response => ({ data: response}));
		}
		return this.get({
			endpoint: `${ENDPOINTS.clients}/${clientId}/${this.endpoint}`,
			queryParams,
		});
	}

	static save(data) {
		const isCreating = data.Id === undefined;
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			if (isCreating) {
				if (data.organizationId !== null) {
					const request = {
						organizationId: data.organizationId,
						createOrganizationContactPayload: {},
					}
					if (Object.hasOwn(data, "organizationId")) {
						request.createOrganizationContactPayload.OrganizationId = data.organizationId;
					}
					if (Object.hasOwn(data, "EmailAddress")) {
						request.createOrganizationContactPayload.EmailAddress = data.EmailAddress;
					}
					if (Object.hasOwn(data, "StreetAddress1")) {
						request.createOrganizationContactPayload.StreetAddress1 = data.StreetAddress1;
					}
					if (Object.hasOwn(data, "City")) {
						request.createOrganizationContactPayload.City = data.City;
					}
					if (Object.hasOwn(data, "Zipcode")) {
						request.createOrganizationContactPayload.Zipcode = data.Zipcode;
					}
					if (Object.hasOwn(data, "StateId")) {
						request.createOrganizationContactPayload.StateId = data.StateId;
					}
					if (Object.hasOwn(data, "MobilePhone")) {
						request.createOrganizationContactPayload.MobilePhone = data.MobilePhone;
					}
					if (Object.hasOwn(data, "AllowTextAlert")) {
						request.createOrganizationContactPayload.AllowTextAlert = data.AllowTextAlert;
					}
					if (Object.hasOwn(data, "IsEmergenyContact")) {
						request.createOrganizationContactPayload.IsEmergenyContact = data.IsEmergenyContact;
					}
					if (Object.hasOwn(data, "CloudFilePictureId")) {
						request.createOrganizationContactPayload.CloudFilePictureId = data.CloudFilePictureId;
					}
					if (Object.hasOwn(data, "IsAdmin")) {
						request.createOrganizationContactPayload.IsAdmin = data.IsAdmin;
					}
					if (Object.hasOwn(data, "IsCoordinator")) {
						request.createOrganizationContactPayload.IsCoordinator = data.IsCoordinator;
					}
					if (Object.hasOwn(data, "LastName")) {
						request.createOrganizationContactPayload.LastName = data.LastName;
					}
					if (Object.hasOwn(data, "LocationId")) {
						request.createOrganizationContactPayload.LocationId = data.LocationId;
					}
					if (Object.hasOwn(data, "FullName")) {
						request.createOrganizationContactPayload.FullName = data.FullName;
					}
					return this.contactsApi.createOrganizationContact(request).then((response) => ({data: response}));
				}
				const request = {
					client: data.clientId,
					createClientContactPayload: {},
				}
				if (Object.hasOwn(data, "organizationId")) {
					request.createClientContactPayload.OrganizationId = data.organizationId;
				}
				if (Object.hasOwn(data, "EmailAddress")) {
					request.createClientContactPayload.EmailAddress = data.EmailAddress;
				}
				if (Object.hasOwn(data, "StreetAddress1")) {
					request.createClientContactPayload.StreetAddress1 = data.StreetAddress1;
				}
				if (Object.hasOwn(data, "City")) {
					request.createClientContactPayload.City = data.City;
				}
				if (Object.hasOwn(data, "Zipcode")) {
					request.createClientContactPayload.Zipcode = data.Zipcode;
				}
				if (Object.hasOwn(data, "StateId")) {
					request.createClientContactPayload.StateId = data.StateId;
				}
				if (Object.hasOwn(data, "MobilePhone")) {
					request.createClientContactPayload.MobilePhone = data.MobilePhone;
				}
				if (Object.hasOwn(data, "AllowTextAlert")) {
					request.createClientContactPayload.AllowTextAlert = data.AllowTextAlert;
				}
				if (Object.hasOwn(data, "IsEmergenyContact")) {
					request.createClientContactPayload.IsEmergenyContact = data.IsEmergenyContact;
				}
				if (Object.hasOwn(data, "CloudFilePictureId")) {
					request.createClientContactPayload.CloudFilePictureId = data.CloudFilePictureId;
				}
				if (Object.hasOwn(data, "IsAdmin")) {
					request.createClientContactPayload.IsAdmin = data.IsAdmin;
				}
				if (Object.hasOwn(data, "IsCoordinator")) {
					request.createClientContactPayload.IsCoordinator = data.IsCoordinator;
				}
				if (Object.hasOwn(data, "LastName")) {
					request.createClientContactPayload.LastName = data.LastName;
				}
				if (Object.hasOwn(data, "LocationId")) {
					request.createClientContactPayload.LocationId = data.LocationId;
				}
				if (Object.hasOwn(data, "FullName")) {
					request.createClientContactPayload.FullName = data.FullName;
				}
				return this.contactsApi.createClientContact(request).then((response) => ({data: response}));
			}
			const updateReq = {
				id: data.Id,
				updateContactPayload: {},
			}
			if (Object.hasOwn(data, "Id")) {
				updateReq.updateContactPayload.Id = data.Id;
			}
			if (Object.hasOwn(data, "EmailAddress")) {
				updateReq.updateContactPayload.EmailAddress = data.EmailAddress;
			}
			if (Object.hasOwn(data, "IsAdmin")) {
				updateReq.updateContactPayload.IsAdmin = data.IsAdmin;
			}
			if (Object.hasOwn(data, "StreetAddress1")) {
				updateReq.updateContactPayload.StreetAddress1 = data.StreetAddress1;
			}
			if (Object.hasOwn(data, "City")) {
				updateReq.updateContactPayload.City = data.City;
			}
			if (Object.hasOwn(data, "Zipcode")) {
				updateReq.updateContactPayload.Zipcode = data.Zipcode;
			}
			if (Object.hasOwn(data, "StateId")) {
				updateReq.updateContactPayload.StateId = data.StateId;
			}
			if (Object.hasOwn(data, "IsExternal")) {
				updateReq.updateContactPayload.IsExternal = data.IsExternal;
			}
			if (Object.hasOwn(data, "FirstName")) {
				updateReq.updateContactPayload.FirstName = data.FirstName;
			}
			if (Object.hasOwn(data, "LastName")) {
				updateReq.updateContactPayload.LastName = data.LastName;
			}
			if (Object.hasOwn(data, "MobilePhone")) {
				updateReq.updateContactPayload.MobilePhone = data.MobilePhone;
			}
			if (Object.hasOwn(data, "AlternatePhone")) {
				updateReq.updateContactPayload.AlternatePhone = data.AlternatePhone;
			}
			if (Object.hasOwn(data, "AllowTextAlert")) {
				updateReq.updateContactPayload.AllowTextAlert = data.AllowTextAlert;
			}
			if (Object.hasOwn(data, "IsEmergenyContact")) {
				updateReq.updateContactPayload.IsEmergenyContact = data.IsEmergenyContact;
			}

			return this.contactsApi.updateContact(updateReq).then(response => ({ data: response }));

		}
		const endpoint = isCreating
			? `${data.organizationId ? ENDPOINTS.organizations : ENDPOINTS.clients}/${data.organizationId || data.clientId}/${this.endpoint}`
			: `${this.endpoint}/${data.Id}`;

		return this.request({
			endpoint,
			method: isCreating ? "POST" : "PUT",
			data,
		});
	}

	static delete(id) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			return this.contactsApi.deleteContact({ id }).then(response => ({ data: response }));
		}
		return this.del({
			endpoint: `${this.endpoint}/${id}`,
		});
	}
}
